import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  ContactSection,
  ContactLayout,
  ContactCopy,
  ContactCard,
  ContactKicker,
  ContactHeading,
  ContactBody,
  ContactEmail,
  ContactForm,
  ContactField,
  ContactLabel,
  ContactInput,
  ContactTextarea,
  ContactError,
  ContactSubmit
} from './ContactUsStyledComponents'

const CONTACT_EMAIL = 'hausmart@hausmart.com'
const RECAPTCHA_SITE_KEY = '6Lchg3UUAAAAACUdWGqDtq3I0XBpdq2lD-OPA0_F'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const isInvalidPhone = value => {
  const digits = value.replace(/\D/g, '')
  return digits.length < 7 || digits.length > 15
}

const ContactUs = () => {
  const intl = useIntl()
  const recaptchaRef = useRef(null)
  const [formName, setFormName] = useState('')
  const [formEmail, setFormEmail] = useState('')
  const [formPhone, setFormPhone] = useState('')
  const [formNameErr, setFormNameErr] = useState(false)
  const [formEmailErr, setFormEmailErr] = useState(false)
  const [formPhoneErr, setFormPhoneErr] = useState(false)
  const [captchaValid, setCaptchaValid] = useState(false)

  useEffect(() => {
    if (!captchaValid) {
      return
    }
    document.getElementById('homepage-contact-submit').click()
  }, [captchaValid])

  const handleNameBlur = () => {
    setFormNameErr(/\d+/.test(formName))
  }

  const handleEmailBlur = () => {
    setFormEmailErr(!EMAIL_PATTERN.test(formEmail))
  }

  const handlePhoneBlur = () => {
    setFormPhoneErr(isInvalidPhone(formPhone))
  }

  const validate = event => {
    const nameInvalid = /\d+/.test(formName)
    const emailInvalid = !EMAIL_PATTERN.test(formEmail)
    const phoneInvalid = isInvalidPhone(formPhone)

    if (nameInvalid || emailInvalid || phoneInvalid) {
      event.preventDefault()
      setFormNameErr(nameInvalid)
      setFormEmailErr(emailInvalid)
      setFormPhoneErr(phoneInvalid)
      return
    }
    if (!captchaValid) {
      event.preventDefault()
      recaptchaRef.current.execute()
    }
  }

  return (
    <ContactSection id="contact">
      <ContactLayout>
        <ContactCopy>
          <ContactKicker>
            <FormattedMessage id="contact.dontBeShy" />
          </ContactKicker>
          <ContactHeading>
            <FormattedMessage id="homepage.contact.heading" />
          </ContactHeading>
          <ContactBody>
            <FormattedMessage id="homepage.contact.body" />
          </ContactBody>
          <ContactEmail href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</ContactEmail>
        </ContactCopy>
        <ContactCard>
        <ContactForm name="contact" method="post" action="/contact/success" onSubmit={validate}>
          <input type="hidden" name="form-name" value="contact" aria-hidden="true" />
          <ContactField>
            <ContactLabel htmlFor="homepage-contact-name">
              <FormattedMessage id="contact.name" />
            </ContactLabel>
            <ContactInput
              id="homepage-contact-name"
              type="text"
              name="name"
              required
              placeholder={intl.formatMessage({ id: 'homepage.contact.nameExample' })}
              onChange={event => setFormName(event.target.value)}
              onBlur={handleNameBlur}
            />
            {formNameErr && (
              <ContactError>
                <FormattedMessage id="contact.invalidName" />
              </ContactError>
            )}
          </ContactField>
          <ContactField>
            <ContactLabel htmlFor="homepage-contact-email">
              <FormattedMessage id="contact.email" />
            </ContactLabel>
            <ContactInput
              id="homepage-contact-email"
              type="email"
              name="email"
              required
              placeholder={intl.formatMessage({ id: 'homepage.contact.emailExample' })}
              onChange={event => setFormEmail(event.target.value)}
              onBlur={handleEmailBlur}
              onInvalid={event => {
                event.preventDefault()
                setFormEmailErr(true)
              }}
            />
            {formEmailErr && (
              <ContactError>
                <FormattedMessage id="contact.invalidEmail" />
              </ContactError>
            )}
          </ContactField>
          <ContactField>
            <ContactLabel htmlFor="homepage-contact-company">
              <FormattedMessage id="contact.company" />
            </ContactLabel>
            <ContactInput
              id="homepage-contact-company"
              type="text"
              name="company"
              required
              placeholder={intl.formatMessage({ id: 'homepage.contact.companyExample' })}
            />
          </ContactField>
          <ContactField>
            <ContactLabel htmlFor="homepage-contact-phone">
              <FormattedMessage id="contact.phoneNumber" />
            </ContactLabel>
            <ContactInput
              id="homepage-contact-phone"
              type="tel"
              name="phone"
              required
              placeholder={intl.formatMessage({ id: 'homepage.contact.phoneExample' })}
              onChange={event => setFormPhone(event.target.value)}
              onBlur={handlePhoneBlur}
            />
            {formPhoneErr && (
              <ContactError>
                <FormattedMessage id="contact.invalidPhoneNumber" />
              </ContactError>
            )}
          </ContactField>
          <ContactField>
            <ContactLabel htmlFor="homepage-contact-message">
              <FormattedMessage id="contact.message" />
            </ContactLabel>
            <ContactTextarea
              id="homepage-contact-message"
              name="message"
              required
              rows="4"
              placeholder={intl.formatMessage({ id: 'homepage.contact.messageExample' })}
            />
          </ContactField>
          <ContactSubmit id="homepage-contact-submit" type="submit">
            <FormattedMessage id="contact.send" />
          </ContactSubmit>
          <ReCAPTCHA
            name="reCaptcha"
            netlify-recaptcha="true"
            ref={recaptchaRef}
            size="invisible"
            onChange={() => setCaptchaValid(true)}
            sitekey={RECAPTCHA_SITE_KEY}
          />
        </ContactForm>
        </ContactCard>
      </ContactLayout>
    </ContactSection>
  )
}

ContactUs.displayName = 'ContactUs'

export default ContactUs
